import React, { useState } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PasswordStrength } from '@/components/ui/password-strength';

interface ManualPasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newPassword: string) => Promise<void>;
  isLoading?: boolean;
}

export const ManualPasswordResetModal: React.FC<ManualPasswordResetModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!newPassword) {
      errors.newPassword = t('validation.required');
    } else if (newPassword.length < 8) {
      errors.newPassword = t('validation.passwordMinLength');
    }

    if (!confirmPassword) {
      errors.confirmPassword = t('validation.required');
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = t('validation.passwordMismatch');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await onConfirm(newPassword);
      handleClose();
    } catch {
      // Error handling is done in the parent component
    }
  };

  const handleClose = () => {
    setNewPassword('');
    setConfirmPassword('');
    setValidationErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop con efecto focal */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal centrado */}
      <div className="relative bg-brand-white rounded-lg shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 ease-out scale-100 opacity-100">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-brand-gray-dark sentence-case">
              {t('pages.users.modals.manualPasswordReset.title')}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            aria-label="Cerrar"
            disabled={isLoading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-brand-gray-dark sentence-case leading-relaxed">
            {t('pages.users.modals.manualPasswordReset.description')}
          </p>

          <div>
            <label className="block text-sm font-medium text-brand-gray-dark">
              {t('pages.users.fields.newPassword')}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder={t('pages.users.placeholders.newPassword')}
                className="w-full pr-10"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                disabled={isLoading}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {newPassword && <PasswordStrength password={newPassword} />}
            {validationErrors.newPassword && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.newPassword}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-gray-dark">
              {t('pages.users.fields.confirmNewPassword')}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder={t('pages.users.placeholders.confirmNewPassword')}
                className="w-full pr-10"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                disabled={isLoading}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {validationErrors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.confirmPassword}</p>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="bg-brand-white text-brand-gray-dark border border-brand-gray-dark hover:bg-brand-gray-dark hover:text-brand-white px-4 py-2 rounded-md transition-colors duration-200 sentence-case"
            disabled={isLoading}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword
            }
            className={`bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 sentence-case ${
              isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
          >
            {isLoading ? t('common.saving') : t('pages.users.modals.manualPasswordReset.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};
